import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Container, Col, Row, Spinner } from "react-bootstrap";
import { getTour } from "../Api/TourApi";
import { Image, Transformation, Placeholder } from "cloudinary-react";
import Lightbox from "../Components/Lightbox";
import AvailabilitySmall from "../Components/Tour/AvailabilitySmall";
import Contents from "../Components/Tour/Contents";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import PageSEO from "../Components/PageSEO";
import {
  MdStairs,
  MdStroller,
  MdTrendingFlat,
  MdNoStroller,
  MdAccessTime,
  MdStar,
  MdGroup,
  MdLocationOn,
} from "react-icons/md";
import { BsFillStarFill } from "react-icons/bs";
import ScrollReveal from "../Components/ScrollReveal";

const GROUP_LABELS = {
  underground: "Underground",
  historic: "Historic",
  haunted: "Haunted",
  foodanddrink: "Food & Drink",
  history: "Historic",
};

const groupLabel = (g) => GROUP_LABELS[g.trim()] || g.trim();

const TourPage = (props) => {
  const [loading, setLoading] = useState(true);
  const [tourInfo, setTourInfo] = useState({});
  const [descExpanded, setDescExpanded] = useState(false);
  const [difficultyExpanded, setDifficultyExpanded] = useState(false);
  const [photoCount, setPhotoCount] = useState(null);
  const history = useHistory();

  const fixedslug = props.match.params.slug.replace(".html", "");
  const current = new Date();

  useEffect(() => {
    let cancelled = false;
    async function fetchTour() {
      const response = await getTour(fixedslug);
      if (!response.data[0]) { history.push("/"); return; }
      if (!cancelled) {
        setTourInfo(response.data[0]["attributes"]);
        setLoading(false);
      }
    }
    fetchTour();
    return () => { cancelled = true; };
  }, [fixedslug, history]);

  const schema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: tourInfo.title,
    description: tourInfo.description,
    brand: { "@type": "Brand", name: "American Legacy Tours" },
    image: "https://res.cloudinary.com/american-legacy-tours/image/upload/f_auto,q_auto" + tourInfo.picturePath,
    offers: {
      "@type": "Offer",
      url: "https://www.americanlegacytours.com/" + fixedslug,
      price: tourInfo.price,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      priceValidUntil: "12/31/" + current.getFullYear(),
    },
  };

  const heroUrl = tourInfo.topPath
    ? `https://res.cloudinary.com/american-legacy-tours/image/upload/c_fill,h_650,w_1800/f_auto,q_auto${tourInfo.topPath}`
    : tourInfo.picturePath
    ? `https://res.cloudinary.com/american-legacy-tours/image/upload/c_fill,h_650,w_1800/f_auto,q_auto${tourInfo.picturePath}`
    : "";

  const groups = tourInfo.groups ? tourInfo.groups.split(",").filter(Boolean) : [];

  return (
    <>
      {loading && (
        <div style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spinner animation="border" role="status" style={{ color: "var(--brand-orange)", width: 48, height: 48 }}>
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      )}

      {!loading && (
        <>
          <PageSEO
            title={`${tourInfo.title} | American Legacy Tours`}
            description={`${tourInfo.description ? tourInfo.description.slice(0, 145).trim() + "…" : `Join ${tourInfo.title} with American Legacy Tours in Cincinnati. Book your spot today!`}`}
            path={`/${fixedslug}`}
            type="product"
            image={tourInfo.picturePath ? `https://res.cloudinary.com/american-legacy-tours/image/upload/c_fill,h_630,w_1200/f_auto,q_auto${tourInfo.picturePath}` : undefined}
            schema={schema}
          />

          {/* ── Hero ──────────────────────────────────── */}
          <div className="tour-hero" style={{ backgroundImage: heroUrl ? `url('${heroUrl}')` : "none", backgroundColor: "var(--brand-dark)" }}>
            <div className="tour-hero__overlay">
              <Container>
                <div className="tour-hero__inner">

                  {/* Portrait image — card-sized, same as main page tour cards */}
                  {tourInfo.picturePath && (
                    <div className="tour-hero__img-side d-none d-lg-block">
                      <div className="tour-hero__img-wrap">
                        <Image
                          cloudName="american-legacy-tours"
                          publicId={tourInfo.picturePath}
                          style={{ width: "100%", display: "block" }}
                          fetch_format="auto"
                          quality="auto"
                        >
                          <Placeholder type="predominant" />
                          <Transformation height="364" width="260" crop="fill" />
                        </Image>
                      </div>
                    </div>
                  )}

                  {/* Text content */}
                  <div className="tour-hero__content">
                  {groups.length > 0 && (
                    <div className="tour-hero__cats">
                      {groups.map((g) => (
                        <span key={g} className="tour-hero__cat">{groupLabel(g)}</span>
                      ))}
                    </div>
                  )}

                  <h1 className="tour-hero__title">{tourInfo.title}</h1>

                  <div className="tour-hero__rating">
                    <span className="tour-hero__stars">
                      <BsFillStarFill /><BsFillStarFill /><BsFillStarFill /><BsFillStarFill /><BsFillStarFill />
                    </span>
                    <span className="tour-hero__rating-text">4.9 / 5</span>
                    <a
                      href="https://www.tripadvisor.com/Attraction_Review-g60993-d4432364-Reviews-American_Legacy_Tours-Cincinnati_Ohio.html"
                      className="tour-hero__rating-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      3,500+ TripAdvisor Reviews
                    </a>
                  </div>

                  <div className="tour-hero__cta-row">
                    {tourInfo.price && (
                      <div>
                        <span className="tour-hero__price">From ${tourInfo.price}</span>
                        <span className="tour-hero__price-label">per person</span>
                      </div>
                    )}
                    <a href={`/${fixedslug}/buy`} className="btn-brand tour-hero__book-btn">
                      Book Now
                    </a>
                  </div>
                  </div>{/* tour-hero__content */}

                </div>{/* tour-hero__inner */}
              </Container>
            </div>{/* tour-hero__overlay */}
          </div>{/* tour-hero */}

          {/* ── Main body ─────────────────────────────── */}
          <div className="tour-page-body">
            <Container>
              <Row>

                {/* ── Left column ── */}
                <Col lg={8}>

                  {/* About this tour */}
                  <ScrollReveal>
                    <section className="tour-section">
                      <h2 className="tour-section__heading">About This Tour</h2>
                      {(() => {
                        const isLong = (tourInfo.description || "").length > 300 || !!tourInfo.description2 || !!tourInfo.description3;
                        return (
                          <>
                            <div style={{ position: "relative" }}>
                              <div className={`tour-description${isLong && !descExpanded ? " tour-description--collapsed" : ""}`}>
                                {tourInfo.description && <p>{tourInfo.description}</p>}
                                {tourInfo.description2 && <p>{tourInfo.description2}</p>}
                                {tourInfo.description3 && <p>{tourInfo.description3}</p>}
                              </div>
                              {isLong && !descExpanded && <div className="tour-description__fade" />}
                            </div>
                            {isLong && (
                              <button className="tour-read-more" onClick={() => setDescExpanded(!descExpanded)}>
                                {descExpanded ? "Show less ↑" : "Read more ↓"}
                              </button>
                            )}
                          </>
                        );
                      })()}
                      {groups.length > 0 && (
                        <div className="tour-group-tags">
                          {groups.map((g) => (
                            <span key={g} className="tour-group-tag">{groupLabel(g)}</span>
                          ))}
                        </div>
                      )}
                    </section>
                  </ScrollReveal>

                  {/* ── Mobile booking panel ── */}
                  <div className="tour-mobile-book d-block d-lg-none">
                    <div className="tour-mobile-book__head">
                      {tourInfo.price && (
                        <div>
                          <span className="tour-mobile-book__price">From ${tourInfo.price}</span>
                          <span className="tour-mobile-book__price-label">per person</span>
                        </div>
                      )}
                      <a href={`/${fixedslug}/buy`} className="btn-brand tour-mobile-book__btn">
                        Book Now
                      </a>
                    </div>
                    <p className="tour-mobile-book__dates">Upcoming Dates</p>
                    <AvailabilitySmall thundertixId={tourInfo.thundertixId} slug={tourInfo.pathTitle} />
                    <a href={`/${fixedslug}/buy`} className="tour-mobile-book__see-all">
                      See All Available Dates &rarr;
                    </a>
                  </div>

                  {/* Tour details */}
                  {tourInfo.difficulty && (
                    <ScrollReveal delay={80}>
                      <section className="tour-section">
                        <h2 className="tour-section__heading">Tour Details</h2>
                        <div className="tour-details-grid">
                          {tourInfo.duration && (
                            <div className="tour-detail-item">
                              <span className="tour-detail-item__icon"><MdAccessTime size="1.5em" /></span>
                              <span className="tour-detail-item__label">Duration</span>
                              <span className="tour-detail-item__value">{tourInfo.duration}</span>
                            </div>
                          )}
                          <div className="tour-detail-item">
                            <span className="tour-detail-item__icon"><MdGroup size="1.5em" /></span>
                            <span className="tour-detail-item__label">Intensity</span>
                            <span className={`tour-detail-item__value${(tourInfo.difficulty || "").length > 30 && !difficultyExpanded ? " tour-detail-value--collapsed" : ""}`}>
                              {tourInfo.difficulty}
                            </span>
                            {(tourInfo.difficulty || "").length > 30 && (
                              <button className="tour-read-more" style={{ fontSize: "0.75rem", marginBottom: 0 }} onClick={() => setDifficultyExpanded(!difficultyExpanded)}>
                                {difficultyExpanded ? "Show less ↑" : "Read more ↓"}
                              </button>
                            )}
                          </div>
                          <div className="tour-detail-item">
                            <span className="tour-detail-item__icon">
                              {tourInfo.stairs ? <MdStairs size="1.5em" /> : <MdTrendingFlat size="1.5em" />}
                            </span>
                            <span className="tour-detail-item__label">Terrain</span>
                            <span className="tour-detail-item__value">
                              {tourInfo.stairs ? "Includes stairs" : "Flat terrain"}
                            </span>
                          </div>
                          <div className="tour-detail-item">
                            <span className="tour-detail-item__icon">
                              {tourInfo.stroller ? <MdStroller size="1.5em" /> : <MdNoStroller size="1.5em" />}
                            </span>
                            <span className="tour-detail-item__label">Stroller</span>
                            <span className="tour-detail-item__value">
                              {tourInfo.stroller ? "Stroller Friendly" : "Not Stroller Friendly"}
                            </span>
                          </div>
                          {tourInfo.twentyone && (
                            <div className="tour-detail-item">
                              <span className="tour-detail-item__icon"><MdStar size="1.5em" /></span>
                              <span className="tour-detail-item__label">Age Requirement</span>
                              <span className="tour-detail-item__value">21+ Only</span>
                            </div>
                          )}
                          {tourInfo.rating && (
                            <div className="tour-detail-item">
                              <span className="tour-detail-item__icon"><BsFillStarFill size="1.4em" style={{ color: "#ffc107" }} /></span>
                              <span className="tour-detail-item__label">Content Rating</span>
                              <span className="tour-detail-item__value">{tourInfo.rating}</span>
                            </div>
                          )}
                        </div>
                        {tourInfo.ratingContent && (
                          <p style={{ marginTop: 14, fontSize: "0.9rem", color: "#666" }}>{tourInfo.ratingContent}</p>
                        )}
                      </section>
                    </ScrollReveal>
                  )}

                  {/* Where to meet */}
                  {tourInfo.startLocationName && (
                    <ScrollReveal delay={60}>
                      <section className="tour-section">
                        <h2 className="tour-section__heading">Where to Meet</h2>
                        <div className="tour-meetingpoint">
                          <div className="tour-meetingpoint__name">
                            <MdLocationOn style={{ color: "var(--brand-orange)", marginRight: 6, verticalAlign: "middle" }} />
                            {tourInfo.startLocationName}
                          </div>
                          <address>
                            {tourInfo.address}<br />
                            {tourInfo.city}, {tourInfo.state} {tourInfo.zip}
                          </address>
                        </div>
                        <iframe
                          title="googlemap"
                          className="tour-map"
                          src={
                            "https://www.google.com/maps/embed/v1/place?q=" +
                            encodeURI(`${tourInfo.address} ${tourInfo.city} ${tourInfo.state} ${tourInfo.zip}`) +
                            "&key=AIzaSyAF0pVwRqx_wSImGwi80X7chUgzKdq1Q_E"
                          }
                          allowFullScreen
                        />
                      </section>
                    </ScrollReveal>
                  )}

                  {/* TripAdvisor social proof */}
                  <ScrollReveal delay={40}>
                    <section className="tour-section">
                      <h2 className="tour-section__heading">Critically Acclaimed</h2>
                      <div className="ta-inline-badge">
                        <Image
                          cloudName="american-legacy-tours"
                          publicId="/img/home/tripadvisor-logo"
                          style={{ height: 36 }}
                          height="36"
                          loading="lazy"
                          fetch_format="auto"
                        >
                          <Placeholder type="predominant" />
                          <Transformation height="36" />
                        </Image>
                        <div>
                          <div style={{ color: "#ffc107", fontSize: "0.95rem" }}>
                            <BsFillStarFill /><BsFillStarFill /><BsFillStarFill /><BsFillStarFill /><BsFillStarFill />
                          </div>
                          <div style={{ fontSize: "0.82rem", color: "#555", marginTop: 2 }}>
                            2022, 2023 &amp; 2024 Travelers&rsquo; Choice Award
                          </div>
                        </div>
                        <a
                          href="https://www.tripadvisor.com/Attraction_Review-g60993-d4432364-Reviews-American_Legacy_Tours-Cincinnati_Ohio.html"
                          rel="noopener noreferrer"
                          target="_blank"
                          className="btn-brand"
                          style={{ fontSize: "0.85rem", padding: "8px 20px" }}
                        >
                          Read Reviews
                        </a>
                      </div>
                    </section>
                  </ScrollReveal>

                  {/* Photo gallery — hidden if tour has no photos */}
                  {photoCount !== 0 && (
                    <ScrollReveal>
                      <section className="tour-section">
                        <h2 className="tour-section__heading">Photos</h2>
                        <Lightbox slug={tourInfo.pathTitle} id={tourInfo.id} onPhotosLoaded={setPhotoCount} />
                      </section>
                    </ScrollReveal>
                  )}

                  {/* Additional content / FAQ */}
                  <Contents slug={tourInfo.pathTitle} id={tourInfo.id} />

                </Col>

                {/* ── Right: booking sidebar ── */}
                <Col lg={4} className="d-none d-lg-block">
                  <div className="tour-booking-card">
                    <div className="tour-booking-card__header">
                      {tourInfo.price ? (
                        <>
                          <span className="tour-booking-card__price">From ${tourInfo.price}</span>
                          <span className="tour-booking-card__price-label">per person</span>
                        </>
                      ) : (
                        <span className="tour-booking-card__price">Reserve Your Spot</span>
                      )}
                    </div>

                    <div className="tour-booking-card__body">
                      <a href={`/${fixedslug}/buy`} className="btn-brand tour-booking-card__cta">
                        Book Now
                      </a>

                      <p className="tour-booking-card__dates-heading">Upcoming Dates</p>
                      <AvailabilitySmall
                        thundertixId={tourInfo.thundertixId}
                        slug={tourInfo.pathTitle}
                      />

                      <a href={`/${fixedslug}/buy`} className="tour-booking-card__see-all">
                        See All Available Dates &rarr;
                      </a>
                    </div>

                    <div className="tour-booking-card__trust">
                      <div className="tour-booking-card__trust-stars">
                        <BsFillStarFill /><BsFillStarFill /><BsFillStarFill /><BsFillStarFill /><BsFillStarFill />
                      </div>
                      <p className="tour-booking-card__trust-text">
                        4.9 · 3,500+ TripAdvisor Reviews
                      </p>
                    </div>
                  </div>

                </Col>

              </Row>
            </Container>
          </div>
        </>
      )}
    </>
  );
};

export default TourPage;
